import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { api, generateQueryKey } from "@/lib/api";
import {
  processQueryParams,
  type QueryParams,
} from "@/lib/process-query-params";

export interface Person {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  job_title: string | null;
  is_employee: boolean;
  opportunity_count: number;
  touchpoint_count: number;
  first_touchpoint_date?: string | null;
  last_touchpoint_date?: string | null;
  // Associated entities (from detail endpoint)
  accounts?: Array<{
    id: string;
    name: string;
    source_record_id: string;
    type?: string | null;
    record_type_name?: string | null;
  }>;
  opportunities?: Array<{
    id: string;
    name: string;
    account_id: string;
    is_won?: boolean | null;
    is_closed?: boolean | null;
    stage_name?: string | null;
    type?: string | null;
    created_date?: string | null;
    close_date?: string | null;
  }>;
  // Opportunity counts (from detail endpoint)
  opportunity_won_count?: number;
  opportunity_open_count?: number;
  // Custom fields will be added dynamically
  [key: string]: unknown;
}

export interface PersonsResponse {
  results: Person[];
  total: number;
  limit: number;
  offset: number;
  client_warning?: string; // Warning from server (e.g., "SORT_ON_METRIC_TOO_MANY_MATCHING")
}

/**
 * Hook for fetching persons with infinite scrolling
 * Leverages React Query's useInfiniteQuery for automatic pagination
 */
export function useInfinitePersons(params?: QueryParams) {
  // Debounce search parameter to avoid excessive API calls
  const debouncedSearch = useDebounce(params?.search, 300);

  // Create params with debounced search
  const debouncedParams = params
    ? { ...params, search: debouncedSearch }
    : undefined;

  // Process query params
  const searchParams = processQueryParams(debouncedParams || {});

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } =
    useInfiniteQuery<PersonsResponse>({
      queryKey: [...generateQueryKey("datahub/persons/"), debouncedParams],
      queryFn: async ({ pageParam = 0 }) => {
        const currentSearchParams = new URLSearchParams(searchParams);
        currentSearchParams.set("offset", String(pageParam));
        return api.get<PersonsResponse>(
          `datahub/persons/?${currentSearchParams.toString()}`,
        );
      },
      getNextPageParam: (lastPage) => {
        const currentOffset = lastPage.offset ?? 0;
        const totalResults = lastPage.total;
        const resultsLength = lastPage.results.length;

        // If we've fetched all results, return undefined to indicate no more pages
        if (currentOffset + resultsLength >= totalResults) {
          return undefined;
        }

        // Otherwise, return the next offset
        return currentOffset + resultsLength;
      },
      initialPageParam: 0,
      enabled: debouncedParams !== undefined,
    });

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ...rest,
  };
}

/**
 * Hook for fetching all persons matching the given filters for CSV export
 * This leverages the same API endpoint and parameter structure as useInfinitePersons
 * Returns a function that can be called to trigger the export, rather than fetching immediately
 */
export function usePersonsExport() {
  const mutation = useMutation<
    Person[],
    Error,
    QueryParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      const allPersons: Person[] = [];
      let offset = 0;
      let hasMoreData = true;

      // Paginate through all persons
      while (hasMoreData) {
        const queryParams: QueryParams = {
          ...params,
          limit: 50000, // Override the limit for export
          offset,
        };

        const searchParams = processQueryParams(queryParams);

        // Fetch the current page of persons
        const response = await api.get<PersonsResponse>(
          `datahub/persons/?${searchParams}`,
        );

        // Add the results to our array
        allPersons.push(...response.results);

        // Check if we should continue fetching
        hasMoreData = allPersons.length < response.total;
        // Increment the offset for the next page
        offset += response.results.length;
      }

      return allPersons;
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportPersons: (params?: QueryParams) => mutation.mutate(params),
    exportPersonsAsync: (params?: QueryParams) => mutation.mutateAsync(params),
  };
}
